import classNames from 'classnames'
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ResizeContext } from 'core-components/context/resize'

import { HOVER_DELAY } from './constants'
import { PopupPositionContext } from './context/PopupPositionContext'
import { getDirectionByPosition } from './helpers/getPopupData'
import { handleByClick, handleByHover } from './helpers/handleBy'
import { useCloseByClickHandler } from './hooks/useCloseByClickHandler'
import { useRecalculatePosition } from './hooks/useRecalculatePosition'
import { IEventCallback, IPopupContentData, TVisibilityTrigger } from './interface/popup'
import { TPopupHorizontal, TPopupVertical } from './interface/position'
import { PopupContext } from './wrapper/Context'
import { Portal } from './wrapper/Portal'

import styles from './AbsolutePopup.module.scss'

export interface IAbsolutePopup {
  /**
   * A Class name for component root node.
   */
  rootClassName?: string
  /**
   * A Class name for component popup node.
   */
  popupClassName?: string
  /**
   * Vertical direction of the popup.
   */
  verticalDirection?: TPopupVertical
  /**
   * Horizontal direction of the popup.
   */
  horizontalDirection?: TPopupHorizontal
  /**
   * Vertical start position of the popup.
   */
  verticalStartPosition?: TPopupVertical
  /**
   * Horizontal start position of the popup.
   */
  horizontalStartPosition?: TPopupHorizontal
  /**
   * Vertical position offset of the popup.
   */
  verticalOffset?: number
  /**
   * Horizontal position offset of the popup.
   */
  horizontalOffset?: number
  /**
   * Width of the popup.
   */
  width?: number
  /**
   * Change the action type to show popup
   */
  visibilityTrigger?: TVisibilityTrigger
  /**
   * Callback function fires when popup visibility changed
   */
  visibilityCallback?: (visible: boolean) => boolean
  /**
   * Callback function fires when onFocus, onBlur, onMouseOver or onMouseLeave event triggered
   */
  eventCallbacks?: IEventCallback
  /**
   * The element that triggers the popup window.
   */
  children: ReactNode
  /**
   * Content for the popup window.
   */
  popupContent: (contentData: IPopupContentData) => ReactNode
  /**
   * Wrap current popup to own context data
   */
  useOwnContext?: boolean
  /**
   * Do not use, for testing purposes only!
   */
  _popupVisible?: boolean // for tests
  /**
   * Root test ID. This property is used for automated tests.
   */
  __rootTestId?: string
  /**
   * Popup test ID. This property is used for automated tests.
   */
  __popupTestId?: string
  /**
   * Target wrapper test ID. This property is used for automated tests.
   */
  __targetTestId?: string
}

export const AbsolutePopup = forwardRef(
  (
    {
      children,
      popupContent,
      visibilityCallback,
      rootClassName,
      popupClassName,
      width,
      eventCallbacks = {},
      verticalStartPosition = TPopupVertical.Top,
      horizontalStartPosition = TPopupHorizontal.Left,
      verticalDirection = TPopupVertical.Bottom,
      horizontalDirection = TPopupHorizontal.Right,
      horizontalOffset = 0,
      verticalOffset = 0,
      visibilityTrigger = TVisibilityTrigger.Click,
      useOwnContext = true,
      _popupVisible,
      __rootTestId,
      __popupTestId,
      __targetTestId,
    }: IAbsolutePopup,
    ref
  ) => {
    const [visible, setVisible] = useState<boolean>(false)
    const focusState = useRef<{ timer: number | null }>({ timer: null })
    const [resizeCountContext, setResizeCountContext] = useState(1)
    const isPopupWasOpenRef = useRef(false)
    const popupRef = useRef<HTMLDivElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const closePopup = useCallback(() => {
      callVisibleSetter(false)
    }, [])
    const unregisterContextCallback = useRef<((closeFunction: () => void) => void) | null>(null)

    const tabIndexData = useMemo(() => {
      return visibilityTrigger !== TVisibilityTrigger.Hover ? { tabIndex: 1 } : {}
    }, [visibilityTrigger])

    const callVisibleSetter = (value: boolean) => {
      const newVisible = visibilityCallback ? visibilityCallback(value) : value
      setVisible(() => newVisible)
    }

    const { popupAttributeData } = useCloseByClickHandler({
      visible,
      rootRef,
      callVisibleSetter,
      visibilityTrigger,
      useOwnContext,
    })

    const { position, popupStyles, popupClasses } = useRecalculatePosition({
      width,
      popupRef,
      popupClassName,
      verticalOffset,
      horizontalOffset,
      verticalStartPosition,
      horizontalStartPosition,
      verticalDirection,
      horizontalDirection,
      rootRef,
      popupContent,
      visible,
      closePopup,
      resizeCountContext,
    })

    const popupData: IPopupContentData = {
      direction: getDirectionByPosition(position, horizontalDirection, verticalDirection),
      startPosition: {
        horizontal: horizontalStartPosition,
        vertical: verticalStartPosition,
      },
      action: {
        close: closePopup,
        focus: () => rootRef.current?.focus(),
      },
    }

    const onHoverHandler = (value: boolean) => {
      if (handleByHover(visibilityTrigger)) {
        const state = focusState.current
        if (state.timer) {
          clearTimeout(state.timer)
        }
        state.timer = window.setTimeout(() => {
          state.timer = null
          callVisibleSetter(value)
        }, HOVER_DELAY)
      }
    }

    const onTargetClick = useCallback(() => {
      if (handleByClick(visibilityTrigger)) {
        callVisibleSetter(true)
      }
    }, [visibilityTrigger, visibilityCallback])

    useImperativeHandle(ref, () => ({
      open: () => callVisibleSetter(true),
      close: closePopup,
      rootWidth: rootRef.current?.clientWidth,
    }))

    useEffect(() => {
      if (visible) {
        isPopupWasOpenRef.current = true
      }
      if (eventCallbacks.onOpen && visible && isPopupWasOpenRef.current) {
        eventCallbacks.onOpen()
      }
      if (eventCallbacks.onClose && !visible && isPopupWasOpenRef.current) {
        eventCallbacks.onClose()
      }
      callVisibleSetter(visible)
    }, [visible, visibilityCallback, eventCallbacks])

    useEffect(() => {
      if (!visible) {
        if (unregisterContextCallback.current) {
          unregisterContextCallback.current(closePopup)
        }
      }
    }, [visible])

    useEffect(() => {
      if (_popupVisible) {
        setVisible(true)
      }
    }, [_popupVisible])

    const renderContent = () => {
      return (
        <div
          className={classNames(rootClassName, styles.root)}
          onMouseOver={() => onHoverHandler(true)}
          onMouseLeave={() => onHoverHandler(false)}
          data-testid={__rootTestId || 'popupRoot'}
        >
          <div
            ref={rootRef}
            data-testid={__targetTestId ?? 'targetWrapper'}
            className={styles.targetWrapper}
            onMouseDown={onTargetClick}
            {...tabIndexData}
            {...popupAttributeData}
          >
            {children}
          </div>
          {visible ? (
            <Portal>
              <PopupContext.Consumer>
                {(popupContext) => {
                  popupContext.register(closePopup)
                  unregisterContextCallback.current = popupContext.unregister
                  return (
                    <ResizeContext.Provider value={[resizeCountContext, setResizeCountContext]}>
                      <div
                        ref={popupRef}
                        className={popupClasses}
                        data-testid={__popupTestId || 'popupWrapper'}
                        style={popupStyles}
                        {...popupAttributeData}
                      >
                        {popupContent(popupData)}
                      </div>
                    </ResizeContext.Provider>
                  )
                }}
              </PopupContext.Consumer>
            </Portal>
          ) : null}
        </div>
      )
    }

    if (!useOwnContext) {
      return renderContent()
    }

    return (
      <PopupPositionContext.Provider value={popupData}>
        {renderContent()}
      </PopupPositionContext.Provider>
    )
  }
)

AbsolutePopup.displayName = 'AbsolutePopup'
